import * as Sentry from '@sentry/react';

import { TripDetails } from '../../../backendsdk';

const CLIP_REQUEST_TAG = 'find_video_clip_request';

interface ClipRequestCommandBody {
    device_id: string;
    start_time: string;
    end_time: string;
    accident_id?: number;
    description: string;
}

export interface ClipRequestSentryContext {
    tripId?: number;
    tripStartTime?: number;
    tripEndTime?: number;
    licensePlate?: string;
    selectSegmentVideoTime?: number;
    selectSegmentVideoPercentPlayed?: number;
    from?: number;
    to?: number;
    comment?: string;
    requestBody?: ClipRequestCommandBody;
    commandId?: number;
}

let clipRequestContext: ClipRequestSentryContext = {};

const addClipRequestBreadcrumb = (message: string, data: Record<string, unknown>) => {
    Sentry.addBreadcrumb({
        category: CLIP_REQUEST_TAG,
        message,
        level: 'info',
        data,
    });
};

export const resetClipRequestSentryContext = () => {
    clipRequestContext = {};
};

const setClipRequestTripContext = (trip: TripDetails) => {
    clipRequestContext = {
        ...clipRequestContext,
        tripId: trip.id,
        tripStartTime: trip.start_time,
        tripEndTime: trip.end_time,
        licensePlate: trip.license_plate,
    };
};

export const recordClipRequestTripSelected = (trip: TripDetails) => {
    resetClipRequestSentryContext();
    setClipRequestTripContext(trip);
    addClipRequestBreadcrumb('Trip selected', {
        trip_id: trip.id,
        license_plate: trip.license_plate,
        trip_start_time: trip.start_time,
        trip_end_time: trip.end_time,
    });
};

export const ensureClipRequestTripContext = (trip: TripDetails) => {
    if (clipRequestContext.tripId === trip.id) {
        return;
    }
    recordClipRequestTripSelected(trip);
};

export const recordClipRequestSelectSegmentClick = (videoTime?: number, percentVideoPlayed?: number) => {
    clipRequestContext.selectSegmentVideoTime = videoTime;
    clipRequestContext.selectSegmentVideoPercentPlayed = percentVideoPlayed;
    addClipRequestBreadcrumb('Select trip segment clicked', {
        video_time: videoTime,
        percent_video_played: percentVideoPlayed,
    });
};

export const recordClipRequestSent = (
    from: number,
    to: number,
    comment: string,
    requestBody: ClipRequestCommandBody,
    commandId: number,
) => {
    const context: ClipRequestSentryContext = {
        ...clipRequestContext,
        from,
        to,
        comment,
        requestBody,
        commandId,
    };

    Sentry.withScope((scope) => {
        scope.setTag('feature', CLIP_REQUEST_TAG);
        scope.setContext('clip_request', context as Record<string, unknown>);
        Sentry.captureMessage('Find video clip request sent', 'info');
    });

    clipRequestContext = {
        tripId: clipRequestContext.tripId,
        tripStartTime: clipRequestContext.tripStartTime,
        tripEndTime: clipRequestContext.tripEndTime,
        licensePlate: clipRequestContext.licensePlate,
    };
};
